import { computed } from 'vue'
import type { ComputedRef } from 'vue'

interface DateFormatPlaceholder {
  year: string
  month: string
  day: string
}

export function useDatePickerLocalization(locale: ComputedRef<string> | string) {
  const normalizedLocale = computed(() => {
    const localeStr = typeof locale === 'string' ? locale : locale.value.toString()
    return localeStr.split('-')[0].toLowerCase()
  })

  const dateFormat = computed(() => {
    const formats: Record<string, string> = {
      'de': 'dd/MM/yyyy',
      'fr': 'dd/MM/yyyy',
      'es': 'dd/MM/yyyy',
      'en': 'MM/dd/yyyy'
    }
    return formats[normalizedLocale.value] || 'dd/MM/yyyy'
  })

  const formatPlaceholder = computed((): DateFormatPlaceholder => {
    const placeholders: Record<string, DateFormatPlaceholder> = {
      'de': { year: 'Jahr', month: 'Monat', day: 'Tag' },
      'fr': { year: 'Année', month: 'Mois', day: 'Jour' },
      'es': { year: 'Año', month: 'Mes', day: 'Día' },
      'en': { year: 'Year', month: 'Month', day: 'Day' }
    }
    return placeholders[normalizedLocale.value] || placeholders['en']
  })

  // Hardcoded like dateFormat above — Kendo's own CLDR-driven format resolution
  // requires loadLocaleData() to have registered that locale's calendar data,
  // which only happens for the single culture the host app boots with (see
  // main.ts / kendoHelper.ts). Hardcoding here makes the TimePicker format
  // correct regardless of which locale's CLDR data actually got loaded.
  const timeFormat = computed(() => {
    const formats: Record<string, string> = {
      'de': 'HH:mm',
      'fr': 'HH:mm',
      'es': 'HH:mm',
      'en': 'hh:mm a'
    }
    return formats[normalizedLocale.value] || 'HH:mm'
  })

  return {
    dateFormat,
    formatPlaceholder,
    timeFormat
  }
}
